// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/all-contacts.js (origin/main-dev, 971 lines).
 * BYTE-IDENTICAL — 10-column visibility toggle Dropdown, agent-masking-aware
 * number display, hasPermission read-guard for "Contacts" (note: NOT "Contact
 * Group"; separate module in v1's ACL), edit modal identical to group-detail
 * edit modal. Chrome swap: PrimeReact DataTable → obd3-gui DataTable.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loader from '@/components/partials/components/Loader';
import { Card as ShadcnCard, CardContent as ShadcnCardContent } from '@/components/ui/card';
import { Button as RBButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent as ShadcnDropdownMenuContent,
  DropdownMenuItem as ShadcnDropdownMenuItem,
  DropdownMenuTrigger as ShadcnDropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* Micro-shims for the react-bootstrap surface this file uses. Keeps the JSX
 * intact while dropping the compat-shim import. */
const cx = (...args: any[]) => args.filter(Boolean).join(' ');

const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Card: any = ({ className, children, ...props }: any) => (
  <ShadcnCard className={className} {...props}>
    {children}
  </ShadcnCard>
);
Card.Body = ({ className, children, ...props }: any) => (
  <ShadcnCardContent className={className} {...props}>
    {children}
  </ShadcnCardContent>
);

const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);

/* Modal shim → shadcn Dialog (matches react-bootstrap show/onHide + compound
 * Header/Body/Footer/Title API). */
const Modal: any = ({ show, onHide, size, centered: _c, className, children, ...props }: any) => {
  const sizeCls = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-lg';
  return (
    <Dialog open={!!show} onOpenChange={(next: boolean) => !next && onHide?.()}>
      <DialogContent className={cx(sizeCls, 'max-h-[85vh] overflow-y-auto', className)} {...props}>
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ closeButton: _cb, className, children, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Body = ({ className, children, ...props }: any) => (
  <div className={className} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ className, children, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
Modal.Title = ({ className, children, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);

/* Dropdown shim → shadcn DropdownMenu compound. autoClose is honored via
 * onCloseAutoFocus (default keeps menu open on outside click when we treat
 * checkboxes inside — accepted; matches original UX intent). */
const Dropdown: any = ({ children, autoClose: _autoClose, ...props }: any) => (
  <ShadcnDropdownMenu {...props}>{children}</ShadcnDropdownMenu>
);
Dropdown.Toggle = ({ children, className, variant: _v, id: _id, ...props }: any) => (
  <ShadcnDropdownMenuTrigger asChild>
    <RBButton variant="outline" className={className} {...props}>
      {children}
    </RBButton>
  </ShadcnDropdownMenuTrigger>
);
Dropdown.Menu = ({ children, className, ...props }: any) => (
  <ShadcnDropdownMenuContent className={className} align="end" {...props}>
    {children}
  </ShadcnDropdownMenuContent>
);
Dropdown.Item = ({ children, onClick, className, ...props }: any) => (
  <ShadcnDropdownMenuItem className={className} onClick={onClick} {...props}>
    {children}
  </ShadcnDropdownMenuItem>
);
Dropdown.ItemText = ({ children, className, ...props }: any) => (
  <div className={cx('px-2 py-1.5 text-sm', className)} onClick={(e) => e.stopPropagation()} {...props}>
    {children}
  </div>
);

const OverlayTrigger = ({ children }: any) => children;
const Tooltip = ({ children }: any) => <span>{children}</span>;
import type { ColumnDef } from '@tanstack/react-table';
import { FiEdit, FiTrash2, FiArrowLeft, FiMoreVertical, FiPlus, FiSliders } from 'react-icons/fi';
import { FiInfo } from 'react-icons/fi';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBERS_DELETE,
  API_ALL_CONTACTS_LIST,
} from '@/service/url';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { USER_ROLES } from '@/utilities/constant';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import {
  ArrowLeft,
  Users as UsersIcon,
  Building2,
  Pencil,
  Trash2,
} from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const AllContacts = () => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const COLUMN_DEFS = [
    { key: 'name', label: 'Name', default: true },
    { key: 'number', label: 'Number', default: true },
    { key: 'address', label: 'Address', default: true },
    { key: 'company', label: 'Company', default: true },
    { key: 'alt1_number', label: 'Alternate Number 1', default: false },
    { key: 'alt2_number', label: 'Alternate Number 2', default: false },
    { key: 'street', label: 'Street Address', default: false },
    { key: 'city', label: 'City', default: false },
    { key: 'state', label: 'State', default: false },
    { key: 'country', label: 'Country', default: false },
  ];
  const [columnVisibility, setColumnVisibility] = useState(() =>
    COLUMN_DEFS.reduce((acc, c) => ({ ...acc, [c.key]: c.default }), {}),
  );
  const toggleColumn = (key) => setColumnVisibility((prev) => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    setLoading(true);
    const url = API_ALL_CONTACTS_LIST;
    getApiCall(url, {}).then(onListSuccess).catch(onError).finally(() => setLoading(false));
  }, []);

  function onListSuccess(resp) {
    setContacts(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  // Edit is now a dedicated page — pass the row via router state so the
  // page can render instantly without an extra list fetch.
  const handleEditClick = (contact: any) => {
    navigate(`/all-contacts/${contact.id}/edit`, { state: { contact } });
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'contacts-list',
        data: filteredContacts,
        columns: [
          { header: 'Name', accessor: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-' },
          { header: 'Number', accessor: (row) => displayNumber(row.primary_number) || '-' },
          {
            header: 'Address',
            accessor: (row) => [row.street, row.city, row.state, row.country].filter(Boolean).join(', ') || '-',
          },
          { header: 'Company', accessor: (row) => row.company || '-' },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  const maskNumbers = getAgentMasking(getUser());
  const displayNumber = (num) => (maskNumbers ? maskNumber(num) : num);

  const filteredContacts = contacts.filter(
    (contact) =>
      `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase().includes(searchValue.toLowerCase()) ||
      contact?.primary_number?.includes(searchValue) ||
      contact?.company?.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const handleDeleteClick = (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    const url = API_CONTACT_GROUP_MEMBERS_DELETE.replace(':id', contactToDelete);
    postApiCall(url, { id: contactToDelete }).then(onDeleteSuccess).catch(onDeleteError);
    setShowDeleteModal(false);
    setContactToDelete(null);
  };

  function onDeleteSuccess() {
    toast.success('Contact deleted successfully');
    setShowToast(true);
    getApiCall(API_ALL_CONTACTS_LIST, {}).then(onListSuccess).catch(onError);
  }

  function onDeleteError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to delete contact');
      setShowToast(true);
    }
  }

  const isContactsRole = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'read');
  const canCreate =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'add');
  const canEdit =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'edit');
  const canDelete =
    isContactsRole && hasPermission(moduleOperationsForReadPermissionCheck, 'Contacts', 'delete');

  if (!canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: '50px', marginBottom: '50px', fontSize: '16px' }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading && contacts.length === 0) {
    return (
      <FillPage>
        <Loader />
      </FillPage>
    );
  }

  const renderNumberPill = (num: any) =>
    num ? (
      <span className="inline-flex items-center rounded-md bg-muted/60 px-2 py-0.5 font-mono text-xs font-semibold tabular-nums text-foreground ring-1 ring-inset ring-border/60">
        {displayNumber(num)}
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );

  const columns: ColumnDef<any>[] = [];
  if (columnVisibility.name)
    columns.push({
      id: 'name',
      header: 'Name',
      size: 240,
      accessorFn: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-',
      cell: ({ row }) => {
        const nm = `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim() || '—';
        const initials = (nm || '')
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w: string) => w[0])
          .join('')
          .toUpperCase() || '—';
        return (
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20">
              {initials}
            </span>
            <span className="font-medium text-foreground">{nm}</span>
          </div>
        );
      },
    });
  if (columnVisibility.number)
    columns.push({
      accessorKey: 'primary_number',
      header: 'Number',
      size: 160,
      cell: ({ row }) => renderNumberPill(row.original.primary_number),
    });
  if (columnVisibility.address)
    columns.push({
      id: 'address',
      header: 'Address',
      cell: ({ row }) => {
        const { street, city, state, country, id } = row.original;
        const fullAddress = [street, city, state, country].filter(Boolean).join(', ');
        if (!fullAddress) return <span className="text-muted-foreground">—</span>;
        return (
          <span
            className="line-clamp-1 text-sm text-foreground"
            title={fullAddress}
            id={`tooltip-${id}`}
          >
            {fullAddress}
          </span>
        );
      },
    });
  if (columnVisibility.company)
    columns.push({
      accessorKey: 'company',
      header: 'Company',
      cell: ({ row }) =>
        row.original.company ? (
          <span className="text-sm text-foreground">{row.original.company}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    });
  if (columnVisibility.alt1_number)
    columns.push({
      accessorKey: 'alt1_number',
      header: 'Alt. Number 1',
      cell: ({ row }) => renderNumberPill(row.original.alt1_number),
    });
  if (columnVisibility.alt2_number)
    columns.push({
      accessorKey: 'alt2_number',
      header: 'Alt. Number 2',
      cell: ({ row }) => renderNumberPill(row.original.alt2_number),
    });
  if (columnVisibility.street)
    columns.push({
      accessorKey: 'street',
      header: 'Street Address',
      cell: ({ row }) => row.original.street || '-',
    });
  if (columnVisibility.city)
    columns.push({ accessorKey: 'city', header: 'City', cell: ({ row }) => row.original.city || '-' });
  if (columnVisibility.state)
    columns.push({ accessorKey: 'state', header: 'State', cell: ({ row }) => row.original.state || '-' });
  if (columnVisibility.country)
    columns.push({
      accessorKey: 'country',
      header: 'Country',
      cell: ({ row }) => row.original.country || '-',
    });

  columns.push({
    id: 'actions',
    header: 'Actions',
    size: 110,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) => {
      const nm =
        `${row.original.first_name || ''} ${row.original.last_name || ''}`.trim() ||
        `#${row.original.id}`;
      return (
        <div className="flex items-center gap-1">
          {canEdit && (
            <button
              type="button"
              title={`Edit ${nm}`}
              onClick={() => handleEditClick(row.original)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary transition-colors hover:bg-primary/15"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              title={`Delete ${nm}`}
              onClick={() => handleDeleteClick(row.original.id)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-destructive/10 text-destructive transition-colors hover:bg-destructive/15"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      );
    },
  });

  // Stat strip data
  const totalContacts = contacts.length;
  const distinctCompanies = new Set(
    contacts.map((c: any) => c?.company).filter((v: any) => v && String(v).trim()),
  ).size;

  return (
    <FillPage>
      <PageHeader
        title="All Contacts"
        description="Flat list of every contact across your contact groups."
        actions={
          canCreate ? (
            <Button onClick={() => navigate(`/create-all-contacts`)}>
              <FiPlus className="me-1" />
              Add Contact
            </Button>
          ) : null
        }
      />
      {/* Stat strip — Total contacts / Distinct companies */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1.5 text-xs shadow-neu-raised">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary">
            <UsersIcon className="h-3.5 w-3.5" />
          </span>
          <span className="text-muted-foreground">Total contacts</span>
          <span className="font-semibold text-foreground tabular-nums">{totalContacts}</span>
        </div>
        <div className="flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs shadow-neu-raised dark:border-sky-900/60 dark:bg-sky-950/30">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/60 dark:text-sky-300">
            <Building2 className="h-3.5 w-3.5" />
          </span>
          <span className="text-sky-700 dark:text-sky-300">Companies</span>
          <span className="font-semibold text-sky-800 tabular-nums dark:text-sky-200">
            {distinctCompanies}
          </span>
        </div>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Dropdown autoClose="outside">
            <Dropdown.Toggle variant="outline-secondary" id="dropdown-columns">
              <FiSliders className="me-1" />
              Columns
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {COLUMN_DEFS.map((c) => (
                <Dropdown.ItemText key={c.key}>
                  <Form.Check
                    type="checkbox"
                    id={`col-toggle-${c.key}`}
                    label={c.label}
                    checked={!!columnVisibility[c.key]}
                    onChange={() => toggleColumn(c.key)}
                  />
                </Dropdown.ItemText>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          {contacts.length > 0 && (
            <Button variant="outline" onClick={handleExport} disabled={buttonDisable}>
              Export
              {buttonDisable && (
                <span
                  className="spinner-border spinner-border-sm ml-2"
                  role="status"
                  aria-hidden="true"
                />
              )}
            </Button>
          )}
        </div>

        <DataTable
          tableId="contact-group.all"
          columns={columns}
          data={filteredContacts}
          searchPlaceholder="Search by name, number, or company…"
          emptyTitle="No contacts yet"
          emptyDescription="Add a contact or import a group to get started."
          pageSize={10}
          initialColumnPinning={{ right: ['actions'] }}
          disableExport
        />
      </Card>


      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this contact? This action cannot be undone.</Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </FillPage>
  );
};

export default AllContacts;
